import { loginPageConfigUrl } from "env";
import fallbackConfigJson from "../config/fallbackLoginPageStyle.json";
import { LoginPageConfig, Localization, TextContent, UrlsConfig } from "types";
import { DEFAULT_LANGUAGE } from "./language.service";

const STRAPI_ORIGIN = loginPageConfigUrl;

// Local fallback styling — deep-merged under whatever Strapi returns, field
// by field, so a missing/partial Strapi response (or a failed fetch) always
// still resolves to a fully-populated config.
const FALLBACK_LOGIN_PAGE_CONFIG = fallbackConfigJson as unknown as LoginPageConfig;

export function deepMergeConfig<T>(primary: unknown, fallback: T): T {
  if (primary === null || primary === undefined) return fallback;
  if (typeof primary !== "object" || Array.isArray(primary)) return primary as T;
  if (typeof fallback !== "object" || fallback === null || Array.isArray(fallback)) return primary as T;

  const result: Record<string, unknown> = { ...(fallback as Record<string, unknown>) };
  for (const key of Object.keys(primary as Record<string, unknown>)) {
    result[key] = deepMergeConfig(
      (primary as Record<string, unknown>)[key],
      (fallback as Record<string, unknown>)[key],
    );
  }
  return result as T;
}

// Resolves a possibly-relative media path (e.g. a Strapi-uploaded logo)
// against the Strapi origin; passes absolute URLs through unchanged.
export function mediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${STRAPI_ORIGIN}${url}`;
}

// Picks the labels for `language` out of Strapi's `localization[]` array,
// falling back to the "en" entry when the requested language isn't present.
// Returns undefined (rather than throwing) when neither is found — the
// deep-merge below still resolves to the local text.json fallback.
export function pickLocalizedLabels(
  localization: Localization[] | undefined,
  language: string,
): TextContent | undefined {
  if (!localization?.length) return undefined;
  const match =
    localization.find((entry) => entry.language?.toLowerCase() === language) ??
    localization.find((entry) => entry.language?.toLowerCase() === DEFAULT_LANGUAGE);
  return match?.labels;
}

export async function fetchLoginPageConfig(
  config_id: string | null,
  language: string = DEFAULT_LANGUAGE,
): Promise<LoginPageConfig> {
  let remote: LoginPageConfig | null = null;

  if (config_id) {
    try {
      const url = `${loginPageConfigUrl}/api/login-page-configs/${config_id}?populate=all`;

      const response = await fetch(url);
      if (response.ok) {
        const payload = (await response.json()) as {
          data?: LoginPageConfig & { urls?: UrlsConfig | null };
        };
        remote = payload?.data ?? null;
        if (remote) {
          remote = { ...remote, labels: pickLocalizedLabels(remote.localization, language) ?? remote.labels };
        }
      } else {
        console.error("Failed to fetch login page config, status:", response.status);
      }
    } catch (error) {
      console.error("Login page config fetch error, falling back to local styling:", error);
    }
  }

  return deepMergeConfig(remote, FALLBACK_LOGIN_PAGE_CONFIG);
}
